import axios from 'axios';
import { format } from 'date-fns';
import Jsona from 'jsona';

import { isValidDate } from '»utils/time';

const contentType = 'application/vnd.api+json';

const getEndpointType = (url) => {
  const re = /([A-Za-z0-9_-]*)$/g;

  return url.split('?')[0].match(re)[0];
};

const errorHandling = (error) => {
  if (!error.response) {
    throw new Error({
      title: 'Falha de conexão.',
      status: '404',
      detail: 'Não foi possivel aceder ao broker.',
    });
  } else {
    // Validar que o title não contem um erro ORA
    // error.response.data.errors.title = parseOra(error.response.data.errors.title);

    throw (
      error.response.data.errors ?? [{ code: error.response.status.toString() }]
    );
  }
};

const forceContentTypeHeader = () => ({
  headers: { 'Content-Type': contentType },
});

const multiPartFormContentTypeHeader = () => ({
  headers: { 'Content-Type': 'multipart/form-data' },
});

const formUrlEncodedTypeHeader = () => ({
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
  },
});

const setData = (url, attributes, { rowid = null, tcn } = {}) => {
  const newAttributes = { ...attributes };

  if (tcn) {
    newAttributes.tcn = tcn;
  }

  return {
    data: {
      type: getEndpointType(url),
      id: rowid,
      attributes: newAttributes,
    },
  };
};

const setDataWebService = (url, attributes, { rowid = null, tcn } = {}) => {
  const newAttributes = { ...attributes };

  if (tcn) {
    newAttributes.tcn = tcn;
  }

  return {
    type: getEndpointType(url),
    id: rowid,
    ...newAttributes,
  };
};

const setQueryString = (attributes) => {
  const query = [];

  Object.keys(attributes).forEach((attr) => {
    let attribute = '';
    const value = attributes[attr];

    if (isValidDate(value)) {
      // TODO: ALTERAR NO FUTURO, NESTE MOMENTO A API NÃO ESTÁ A SUPORTAR FILTROS DE DATA

      const createDate =
        typeof value === 'string'
          ? format(new Date(value), 'yyyy-MM-dd')
          : format(value, 'yyyy-MM-dd');

      attribute = `${attr}=${createDate}`;
    } else {
      attribute = `${attr}=${encodeURIComponent(attributes[attr]).replace(
        /\+/g,
        '%2B',
      )}`;
    }

    query.push(attribute);
  });

  return query.join('&');
};

const successHandling = (response) => {
  // Response Create success, in this case no data is return
  if (response.status === 204) {
    return null;
  }

  // Force and empty array in case of data is null, this remove the necessity of test null in all views
  if (response.data.data === null) {
    response.data.data = [];
  }

  // Every request that uses Json.api is treat with Jsona.
  if (
    response.headers['content-type'].indexOf('application/vnd.api+json') !== -1
  ) {
    // Business errors: OK request status code, but error in payload
    if (response.data.errors) {
      return response.data;
    }

    return new Jsona().deserialize(response.data);
  }

  // All other requests content-types
  return response;
};

const successWebServiceHandling = (response) => {
  // Response Create success, in this case no data is return
  if (response.status === 204) {
    return null;
  }

  // Force and empty array in case of data is null, this remove the necessity of test null in all views
  if (response.data.data === null) {
    response.data.data = [];
  }

  // Every request that uses Json.api is treat with Jsona.
  if (response.headers['content-type'].indexOf('application/json') !== -1) {
    // Business errors: OK request status code, but error in payload
    if (response.data.errors) {
      return response.data;
    }

    return new Jsona().deserialize(response.data);
  }

  // All other requests content-types
  return response;
};

const successFullHandling = (response) => {
  // Response Create success, in this case no data is return
  if (response.status === 204) {
    return null;
  }

  // Force and empty array in case of data is null, this remove the necessity of test null in all views
  if (response.data.data === null) {
    response.data.data = [];
  }

  // Every request that uses Json.api is treat with Jsona.
  if (
    response.headers['content-type'].indexOf('application/vnd.api+json') !== -1
  ) {
    // Business errors: OK request status code, but error in payload
    if (response.data.errors) {
      return response.data;
    }

    return {
      data: new Jsona().deserialize(response.data),
      included: response.data.included,
    };
  }

  // All other requests content-types
  return response;
};
export const request = {
  get: (url, query, configs) =>
    axios
      .get(url, { params: { ...query }, ...configs })
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex)),
  getWebService: (url, query, configs) =>
    axios
      .get(url, { params: { ...query }, ...configs })
      .then((response) => successWebServiceHandling(response))
      .catch((ex) => errorHandling(ex)),
  getFull: (url, query, configs) =>
    axios
      .get(url, { params: { ...query }, ...configs })
      .then((response) => successFullHandling(response))
      .catch((ex) => errorHandling(ex)),
  downloadFile: (url, query) =>
    axios
      .get(url, { params: { ...query }, responseType: 'blob' })
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex)),
  post: (url, attributes) =>
    axios
      .post(url, setData(url, attributes), forceContentTypeHeader())
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex)),
  postWebService: (url, attributes) =>
    axios
      .post(url, setDataWebService(url, attributes), forceContentTypeHeader())
      .then((response) => successWebServiceHandling(response))
      .catch((ex) => errorHandling(ex)),
  postQueryString: (url, attributes) =>
    axios
      .post(`${url}?${setQueryString(attributes)}`)
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex)),
  postMultiPartForm: (url, attributes) =>
    axios
      .post(url, setData(url, attributes), multiPartFormContentTypeHeader())
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex)),
  postFormUrlEncoded: (url, attributes) =>
    axios
      .post(url, attributes, formUrlEncodedTypeHeader())
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex)),
  uploadFile: (url, file, fileKey) => {
    const formData = new FormData();
    formData.append(fileKey || 'file', file);

    return axios({
      method: 'post',
      url,
      data: formData,
      config: { headers: { 'Content-Type': 'multipart/form-data' } },
    })
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex));
  },
  put: (url, attributes) =>
    axios
      .put(url, setData(url, attributes), forceContentTypeHeader())
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex)),
  update: (url, rowid, attributes, tcn) =>
    axios
      .put(
        `${url}?updatedfields=${Object.keys(attributes)}`,
        setData(url, attributes, { rowid, tcn }),
        forceContentTypeHeader(),
      )
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex)),

  delete: (url, rowid, tcn) =>
    axios
      .delete(url, {
        headers: { 'Content-Type': contentType },
        data: setData(url, {}, { rowid, tcn }),
      })
      .then((response) => successHandling(response))
      .catch((ex) => errorHandling(ex)),

  /**
   * Métodos para fazer set de Headers do axios
   */

  /**
   *  Definição dos headers associados à authenticação
   */
  setAuth: (user, token) => {
    axios.defaults.headers.common['Ftech-User'] = user;
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  },
  setOptionId: (optionId) => {
    axios.defaults.headers.common['Ftech-Option-Id'] = optionId;
  },

  setTrackingId: (trackingId) => {
    axios.defaults.headers.common['Ftech-Tracking-Id'] = trackingId;
  },
  setTracking: (trackingId, optionId) => {
    axios.defaults.headers.common['Ftech-Tracking-Id'] = trackingId;
    axios.defaults.headers.common['Ftech-Option-Id'] = optionId;
  },
  setCulture: ({ languageId, languageCode }) => {
    axios.defaults.headers.common['Ftech-Language'] = languageId;
    axios.defaults.headers.common['Ftech-Culture'] = languageCode;
  },
  setCompany: (company) => {
    axios.defaults.headers.common['Ftech-Company'] = company;
  },
  setStateless: () => {
    axios.defaults.headers.common['Ftech-Stateless'] = 'Y';
  },
  setStateful: () => {
    axios.defaults.headers.common['Ftech-Stateless'] = 'N';
  },
  setContentType: (newContentType) => {
    axios.defaults.headers.common.Accept = newContentType;
    axios.defaults.headers.common['Content-Type'] = newContentType;
  },
  setDefaultContentType: () => {
    axios.defaults.headers.common.Accept = contentType;
    axios.defaults.headers.common['Content-Type'] = contentType;
  },
  setDefaultHeaders: () => {
    axios.defaults.headers.common.Accept = 'application/vnd.api+json';
    axios.defaults.headers.common['Content-Type'] = 'application/vnd.api+json';

    // clientId defaults to "SBS_UI"
    axios.defaults.headers.common['Ftech-Client-Id'] = 'CH_TRLT_UI';

    axios.defaults.headers.common['Ftech-Stateless'] = 'Y';

    // TODO: Passar a utilizar a versão do modulo
    axios.defaults.headers.common['Ftech-Client-Version'] = '1.0.0.0';
  },
  clearHeaders: () => {
    delete axios.defaults.headers.common['Ftech-User'];
    delete axios.defaults.headers.common.Authorization;
    delete axios.defaults.headers.common['Ftech-Action-Id'];
    delete axios.defaults.headers.common['Ftech-Tracking-Id'];
    delete axios.defaults.headers.common['Ftech-Option-Id'];
    delete axios.defaults.headers.common['Ftech-Language'];
    delete axios.defaults.headers.common['Ftech-Culture'];
    delete axios.defaults.headers.common['Ftech-Company'];
  },
};
